import { useGetFaireAcademyPageState } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { useGetStoreType } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetStoreType";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  useHeaderImagesPartialRetailersCafeRestaurantDefault,
  useHeaderImagesPartialRetailersSpaSalonDefault,
  IHeaderImage,
  IHeaderImageDimensions,
  getRotatingWords,
  useHeaderImagesPartialRetailersCafeRestaurantRotating,
  useHeaderImagesPartialRetailersSpaSalonRotating,
  getDreamPositioning,
  getWordPositioning,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/HeaderCard/utils";
import { SignUpButton } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/SignUpButton";
import { OWF_BACKGROUND } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/imagesData";
import { OWF_SECTION_NAME } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/types";
import { Core } from "@faire/design-tokens";
import { trackOpenWithFaireFaireAcademySectionView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademySection";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { getLocaleKey } from "@faire/web/common/serialized-data/getLocaleKey";
import { StoreType } from "@faire/web/common/StoreType";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled, { css, keyframes } from "styled-components";

const getIsEnglish = () => getLocaleKey()?.language === Language.ENG;

export const HeaderCardPartialRetailers: React.FC = () => {
  const { pageState } = useGetFaireAcademyPageState();
  const ref = useDoActionWhenVisible(() =>
    trackOpenWithFaireFaireAcademySectionView(
      pageState,
      OWF_SECTION_NAME.HEADER_CARD
    )
  );

  return (
    <HeaderWrapper ref={ref} data-test-id="open-with-faire-academy-title">
      <HeaderImages />
      <SlideIn offset="50px">
        <HeaderContent>
          <HeaderTitle />
          <HeaderBody />
          <LoaderDots />
        </HeaderContent>
      </SlideIn>
    </HeaderWrapper>
  );
};

const getImageDimensions = (
  dimensions: {
    [viewport: string]: IHeaderImageDimensions;
  },
  isMobile: boolean,
  isTablet: boolean,
  isDesktop: boolean
) => {
  if (isMobile) {
    return dimensions.mobile;
  } else if (isTablet) {
    return dimensions.tablet;
  } else if (isDesktop) {
    return dimensions.desktop;
  } else {
    return dimensions.xlarge;
  }
};

const HeaderImages: React.FC = () => {
  const { isMobile, isTablet, isDesktop } = useViewport();
  const { storeType } = useGetStoreType();
  const headerImagesPartialRetailersCafeRestaurantDefault =
    useHeaderImagesPartialRetailersCafeRestaurantDefault();
  const headerImagesPartialRetailersSpaSalonDefault =
    useHeaderImagesPartialRetailersSpaSalonDefault();
  const headerImagesPartialRetailersCafeRestaurantRotating =
    useHeaderImagesPartialRetailersCafeRestaurantRotating();
  const headerImagesPartialRetailersSpaSalonRotating =
    useHeaderImagesPartialRetailersSpaSalonRotating();

  if (getIsEnglish()) {
    const rotatingHeaderImages =
      storeType === StoreType.CAFE_OR_RESTAURANT
        ? headerImagesPartialRetailersCafeRestaurantRotating
        : headerImagesPartialRetailersSpaSalonRotating;

    return (
      <ImagesWrapper>
        {rotatingHeaderImages.map(
          (imageData: IHeaderImage[], index: number) => (
            <AnimatedImageSection key={imageData[0]?.src} $index={index}>
              <ImageSection imageData={imageData} />
            </AnimatedImageSection>
          )
        )}
      </ImagesWrapper>
    );
  } else {
    const defaultHeaderImages =
      storeType === StoreType.CAFE_OR_RESTAURANT
        ? headerImagesPartialRetailersCafeRestaurantDefault
        : headerImagesPartialRetailersSpaSalonDefault;

    return (
      <ImagesWrapper>
        {defaultHeaderImages.map((image: IHeaderImage) => {
          const imageDimensions = getImageDimensions(
            image.dimensions,
            isMobile,
            isTablet,
            isDesktop
          );

          return (
            <StyledImage
              key={image.src}
              src={image.src}
              $zIndex={image.zIndex}
              style={{
                height: imageDimensions?.height,
              }}
              $top={imageDimensions?.top}
              $left={imageDimensions?.left}
              $right={imageDimensions?.right}
              role="presentation"
            />
          );
        })}
      </ImagesWrapper>
    );
  }
};

const ImageSection: React.FC<{
  imageData: IHeaderImage[];
}> = ({ imageData }) => {
  const { isMobile, isTablet, isDesktop } = useViewport();

  return imageData.map((image: IHeaderImage) => {
    const imageDimensions = getImageDimensions(
      image.dimensions,
      isMobile,
      isTablet,
      isDesktop
    );

    return (
      <StyledImage
        key={image.src}
        src={image.src}
        $zIndex={image.zIndex}
        style={{
          height: imageDimensions?.height,
        }}
        $top={imageDimensions?.top}
        $left={imageDimensions?.left}
        $right={imageDimensions?.right}
        role="presentation"
      />
    );
  });
};

const HeaderTitle: React.FC = () => {
  return (
    <>
      <Typography
        as="h1"
        color={Core.surface.inverse}
        variant={{
          mobileAndAbove: "paragraphSansMedium",
          desktopAndAbove: "subheadingSansMedium",
        }}
      >
        <StrictLocalMsg
          id="+vVy5g"
          defaultMessage="Open with Faire"
          description={{
            text: "Name of a program we offer for helping opening retailers to start their business",
          }}
        />
      </Typography>
      <Spacer
        height={{
          mobileAndAbove: "2x",
          desktopAndAbove: "3x",
        }}
      />
      <Typography
        align="center"
        color={Core.surface.inverse}
        variant={{
          mobileAndAbove: "displayMSerifRegular",
          tabletAndAbove: "displayLSerifRegular",
          desktopAndAbove: "displayXLSerifRegular",
        }}
      >
        <TitleText />
      </Typography>
    </>
  );
};

const TitleText: React.FC = () => {
  const { isMobile, isTablet } = useViewport();
  const { storeType } = useGetStoreType();

  const rotatingWords = getRotatingWords(storeType);
  const dreamPositioning = getDreamPositioning(storeType, isMobile, isTablet);
  const wordPositioning = getWordPositioning(storeType, isMobile, isTablet);
  const hasAnimation = rotatingWords && dreamPositioning && wordPositioning;

  if (!getIsEnglish() || !storeType || !hasAnimation) {
    return (
      <NonEnglishTitleTextWrapper>
        <StrictLocalMsg
          id="eJqlnj"
          defaultMessage="Dream business, here we come"
          description={{
            text: "Header card title for Open with Faire, a program we offer for helping opening retailers to start their business",
            img: "https://cdn.faire.com/fastly/53d11f3c819c13a24c594814faa7ce20557ae6de72e2904598554fe5f4be8e57.png",
          }}
        />
      </NonEnglishTitleTextWrapper>
    );
  }

  return (
    <>
      <RotatingWords>
        <DreamWord $dreamPositioning={dreamPositioning}>Dream&nbsp;</DreamWord>
        {rotatingWords.map((rotatingWord, index) => (
          <Word
            key={rotatingWord}
            $index={index}
            $wordPositioning={wordPositioning}
          >
            {rotatingWord}
          </Word>
        ))}
      </RotatingWords>
      <br />
      here we come
    </>
  );
};

const HeaderBody: React.FC = () => {
  if (isLoggedInRetailer()) {
    return (
      <HeaderBodyColumn>
        <Spacer height="3x" />
        <Typography
          color={Core.surface.inverse}
          variant={{
            mobileAndAbove: "paragraphSansRegular",
            desktopAndAbove: "subheadingSansRegular",
          }}
        >
          <StrictLocalMsg
            id="BY/csa"
            defaultMessage="Starting a business is tough enough as it is, so we get why diving into retail might feel overwhelming. But here at Faire, our goal is to make it a breeze to expand into retail and grow your revenue. "
            description={{
              text: "Motivation statement for Open with Faire, a program we offer for helping opening retailers to start their business",
              img: "https://cdn.faire.com/fastly/53d11f3c819c13a24c594814faa7ce20557ae6de72e2904598554fe5f4be8e57.png",
            }}
          />
        </Typography>
      </HeaderBodyColumn>
    );
  }

  return (
    <>
      <Spacer
        height={{
          mobileAndAbove: "5x",
          desktopAndAbove: "6x",
        }}
      />
      <SignUpButton source={OWF_SECTION_NAME.HEADER_CARD} />
    </>
  );
};

const LoaderDots: React.FC = () => {
  const { storeType } = useGetStoreType();
  const rotatingWords = getRotatingWords(storeType);

  if (!getIsEnglish() || !rotatingWords) {
    return null;
  }

  return (
    <AnimatedLoaderWrapper>
      <svg
        xmlns="https://www.w3.org/2000/svg"
        width="78"
        height="8"
        viewBox="0 0 78 8"
        fill="#DFE0E1"
        style={{ float: "right" }}
      >
        {rotatingWords.map((rotatingWord, index) => (
          <Loader
            key={`${rotatingWord}`}
            cx={4 + index * 14}
            cy={4}
            r={4}
            $index={index}
          />
        ))}
      </svg>
    </AnimatedLoaderWrapper>
  );
};

const SWITCH_TIME = 5;
const ROTATING_WORDS_LENGTH = 4;
const TOTAL_LENGTH = ROTATING_WORDS_LENGTH * SWITCH_TIME;

const FINISH_FADE_IN = 0.1 * SWITCH_TIME;
const START_FADE_OUT = 0.9 * SWITCH_TIME;
const FINISH = 1.0 * SWITCH_TIME;
const POST_FINISH = 1.1 * SWITCH_TIME;

const FINISH_FADE_IN_PCT = ((FINISH_FADE_IN / TOTAL_LENGTH) * 100).toFixed(2);
const START_FADE_OUT_PCT = ((START_FADE_OUT / TOTAL_LENGTH) * 100).toFixed(2);
const FINISH_PCT = ((FINISH / TOTAL_LENGTH) * 100).toFixed(2);
const POST_FINISH_PCT = ((POST_FINISH / TOTAL_LENGTH) * 100).toFixed(2);

const rotateImages = keyframes`
  0% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
  ${FINISH_FADE_IN_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${START_FADE_OUT_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(-30px);
    -webkit-animation: translateY(-30px);
    -ms-transform: translateY(-30px);
  }
  ${POST_FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
`;

const rotateDreamWord = ($dreamPositioning: string[]) => keyframes`
  0% {
    left: ${$dreamPositioning[0]};
  }
  22.5% {
    left: ${$dreamPositioning[0]};
  }

  25% {
    left: ${$dreamPositioning[1]};
  }
  47.5% {
    left: ${$dreamPositioning[1]};
  }

  50% {
    left: ${$dreamPositioning[2]};
  }
  72.5% {
    left: ${$dreamPositioning[2]};
  }

  75% {
    left: ${$dreamPositioning[3]};
  }
  97.5% {
    left: ${$dreamPositioning[3]};
  }

  100% {
    left: ${$dreamPositioning[0]};
  }
`;

const rotateWords = keyframes`
  0% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
  ${FINISH_FADE_IN_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${START_FADE_OUT_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(-30px);
    -webkit-animation: translateY(-30px);
    -ms-transform: translateY(-30px);
  }
  ${POST_FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
`;

const rotateLoader = keyframes`
  0% {
    fill: ${Core.border.subdued};
  }
  ${FINISH_FADE_IN_PCT}% {
    fill: ${Core.border.inverse};
  }
  ${START_FADE_OUT_PCT}% {
    fill: ${Core.border.inverse};
  }
  ${FINISH_PCT}% {
    fill: ${Core.border.subdued};
  }
  ${POST_FINISH_PCT}% {
    fill: ${Core.border.subdued};
  }
  100% {
    fill: ${Core.border.subdued};
  }
`;

const HeaderWrapper = styled(Column).attrs({
  align: "center",
  justify: {
    mobileAndAbove: "end",
    tabletAndAbove: "center",
  },
})`
  height: 613px;
  width: 100%;
  background-image: url(${OWF_BACKGROUND});

  ${responsiveMediaQueries.tablet} {
    height: 525px;
  }
  ${responsiveMediaQueries.mobile} {
    height: 652px;
  }
`;

const ImagesWrapper = styled.div`
  ${responsiveMediaQueries.xLargeAndAbove} {
    position: relative;
    width: 1440px;
    height: 0;
  }
  ${responsiveMediaQueries.mobile} {
    position: relative;
    width: 320px;
    height: 0;
  }
`;

const AnimatedImageSection = styled.div<{ $index: number }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  visibility: hidden;
  opacity: 0;
  animation: ${rotateImages} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -webkit-animation: ${rotateImages} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -ms-animation: ${rotateImages} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
`;

const StyledImage = styled.img<{
  $zIndex?: number;
  $top?: string;
  $left?: string;
  $right?: string;
}>`
  position: absolute;
  display: inline;

  ${(props) =>
    props.$zIndex
      ? css`
          z-index: ${props.$zIndex};
        `
      : css`
          z-index: 1;
        `};
  ${(props) => css`
    top: ${props?.$top};
    left: ${props?.$left};
    right: ${props?.$right};
  `}
`;

const HeaderContent = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
    margin-bottom: ${getSpacing("8x")};
  }
`;

const NonEnglishTitleTextWrapper = styled(Row)`
  max-width: 515px;

  ${responsiveMediaQueries.tablet} {
    max-width: 400px;
  }
  ${responsiveMediaQueries.mobile} {
    max-width: 340px;
  }
`;

const DreamWord = styled.span<{ $dreamPositioning: string[] }>`
  position: absolute;
  display: inline;
  overflow: hidden;
  animation: ${(props) => css`
    ${rotateDreamWord(props.$dreamPositioning)} 20s linear infinite
  `};
`;

const Word = styled.span<{ $index: number; $wordPositioning: string[] }>`
  position: absolute;
  left: ${(props) => props.$wordPositioning[props.$index]};
  display: inline;
  overflow: hidden;
  visibility: hidden;
  opacity: 0;
  animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -webkit-animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -ms-animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
`;

const RotatingWords = styled.span`
  display: inline;
`;

const HeaderBodyColumn = styled(Column)`
  text-align: center;
  max-width: 417px;

  ${responsiveMediaQueries.tablet} {
    width: 296px;
  }
  ${responsiveMediaQueries.mobile} {
    max-width: 257px;
  }
`;

const AnimatedLoaderWrapper = styled.div`
  position: absolute;
  bottom: -70px;
  ${responsiveMediaQueries.mobile} {
    bottom: 30px;
  }
`;

const Loader = styled.circle<{ $index: number }>`
  fill: ${Core.border.subdued};
  animation: ${rotateLoader} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -webkit-animation: ${rotateLoader} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -ms-animation: ${rotateLoader} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
`;
